
import { Component, Vue, Watch } from 'vue-property-decorator'
import Project from "@/types/project/project"
import { ProjectService } from "@/services/timetrack/ProjectService"
import { TimesService } from "@/services/timetrack/TimesService"
import { ExpenditureService } from "@/services/timetrack/ExpenditureService"
import Tracking from "@/types/tracking/tracking"
import { exportExpendituresToCsv, exportTrackedTimesToCsv } from "@/views/Timetrack/Export/CSVexport"
import { prepareExpenditureData, prepareTrackingData } from "@/views/Timetrack/Export/Prepare"
import { DateAndTimeHelper } from "@/helpers/DateAndTimeHelper"
import { exportEntries } from "@/views/Timetrack/Export/ExcelExport"
import TimeRange from "@/types/time/timeRange"
import DateRangeDialog from "@/components/DateAndTime/DateRangeDialog.vue"
import Pdf from "@/components/Export/Pdf.vue"
import Collaborator from "@/types/collaborator/Collaborator"
import { CollaboratorService } from "@/services/timetrack/CollaboratorService"
import { AccountService } from "@/services/account/AccountService"

@Component({
  filters: {
  },
  components: {
    Pdf,
    DateRangeDialog
  }
})

export default class Export extends Vue {
  private loading = false
  private exportCollaborators = false
  private dateRangePickerDialog = false
  private exportFormat: string | null = null
  private timeRange: TimeRange | null = null
  private selectedTimePeriod: string | null = 'last_month'
  private downloadPdf = false
  private projectsToBeDownloaded: string[] | null = null
  private timePeriods = [
    { name: "last_month", translation: this.$t("timetrack.export.exportPeriods.lastMonth") },
    { name: "month", translation: this.$t("timetrack.export.exportPeriods.thisMonth") },
    { name: "custom", translation: this.$t("timetrack.export.exportPeriods.custom") },
    { name: "project", translation: this.$t("timetrack.export.exportPeriods.project") }
  ]

  private selectedProjects: Project[] = []
  private selectedProjectsWithCollaborators: Project[] = []
  private projects: Project[] | null = null
  private collaborators: Collaborator[] = []
  private projectService = new ProjectService()
  private timeService = new TimesService()
  private accountService = new AccountService()
  private expenditureService = new ExpenditureService()
  private dateTimeHelper = new DateAndTimeHelper()
  private collaboratorService = new CollaboratorService()

  created () {
    this.fetchProjects()
  }

  @Watch('selectedProjects')
  private selectedProjectsChanged () {
    if (this.selectedProjects.length === 0) {
      this.collaborators = []
      return
    }
    const projectIds = this.selectedProjects.map((project) => { return project.id })
    if (!projectIds || projectIds.length < 0) return
    this.loading = true
    this.collaboratorService.getCollaboratorsForProjects(projectIds)
      .then((collaborators) => {
        this.collaborators = collaborators
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.loading = false
      })
  }

  private get disabled () {
    return !this.selectedProjects || this.selectedProjects.length < 1 || !this.selectedTimePeriod ||
      (this.selectedTimePeriod === 'custom' && !this.timeRange) || this.loading
  }

  private get timeRangeLabel () {
    if (!this.timeRange || this.selectedTimePeriod !== 'custom' || !this.timeRange.start || !this.timeRange.end) return
    const startDate = new Date(this.timeRange.start).setHours(0, 0, 0, 0)
    const endDate = new Date(this.timeRange.end).setHours(0, 0, 0, 0)
    if (startDate === endDate) return this.dateTimeHelper.getReadableDateFromStartingTime(this.timeRange.start)
    return this.dateTimeHelper.getReadableDateFromStartingTime(this.timeRange.start) + ' - ' +
      this.dateTimeHelper.getReadableDateFromStartingTime(this.timeRange.end)
  }

  private setTimeRange (range: TimeRange) {
    this.timeRange = range
    this.dateRangePickerDialog = false
  }

  private fetchProjects () {
    this.loading = true
    this.projectService.getAllProjects()
      .then((projects) => {
        this.projects = projects
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.loading = false
      })
  }

  private fetchCollaboratorProjects () {
    const collaboratorProjectIds = this.collaborators.map((collab) => { return collab.collaboratorProjectId })
    if (collaboratorProjectIds.length > 0) {
      return this.projectService.getProjectsByIds(collaboratorProjectIds)
    }
    return Promise.resolve([])
  }

  private exportProject () {
    this.loading = true
    if (this.selectedTimePeriod === 'month') {
      this.timeRange = this.dateTimeHelper.getMonthRangeInMilliseconds(new Date().getMonth())
    } else if (this.selectedTimePeriod === 'last_month') {
      this.timeRange = this.dateTimeHelper.getMonthRangeInMilliseconds(new Date().getMonth() - 1, true)
    } else if (this.selectedTimePeriod !== 'custom') {
      this.timeRange = null
    }
    if (!this.selectedProjects || this.selectedProjects.length < 1) return
    this.fetchCollaboratorProjects()
      .then((projects) => {
        if (this.exportCollaborators) {
          this.selectedProjectsWithCollaborators = this.selectedProjects.concat(projects)
        } else {
          this.selectedProjectsWithCollaborators = this.selectedProjects
        }
        this.selectedProjectsWithCollaborators.forEach((project, index) => {
          if (!project.id) return
          if (project.trackedTimes) project.trackedTimes = null
          this.projectService.getUserIdFromProject(project.id)
            .then((userId) => {
              if (!project.id) return
              this.collectData(project.id, this.timeRange, userId)
                .then((timesWithExpenditures) => {
                  if (!project || !timesWithExpenditures) return
                  project.trackedTimes = timesWithExpenditures
                })
                .then(() => {
                  if (this.exportFormat === 'csv') {
                    this.exportCSV(project, userId)
                  } else if (this.exportFormat === 'xlsx') {
                    this.exportXLSX(project, userId)
                  } else if (this.exportFormat === 'pdf' && index === (this.selectedProjectsWithCollaborators.length - 1)) {
                    this.downloadPdf = true
                  }
                }).catch((error) => {
                  this.$store.dispatch('error/setError', error)
                }).finally(() => {
                  if (this.exportFormat !== 'pdf') {
                    this.loading = false
                  }
                })
            })
        })
      })
  }

  private collectData (projectId: string, timeRange: TimeRange | null, userId?: string) {
    return this.fetchTrackedTimes(projectId, timeRange, userId)
      .then((times) => {
        return this.fetchExpenditures(times)?.then((expenditures) => {
          times = times.map((tracking) => {
            tracking.expenditures = expenditures.filter((expenditure) => { return expenditure.trackingId === tracking.id })
            return tracking
          })
          return times
        })
      })
  }

  private fetchTrackedTimes (projectId: string, timeRange: TimeRange | null, userId?: string) {
    if (timeRange) {
      return this.timeService.getInTimeRangeForProject(projectId, timeRange, userId)
    } else {
      return this.timeService.getAllForProject(projectId, userId)
    }
  }

  private fetchExpenditures (times: Tracking[]) {
    const trackingIds = times.map((tracking) => { return tracking.id })
    if (!trackingIds || trackingIds.length < 1) return
    return this.expenditureService.getExpendituresForTrackings(trackingIds as string[])
  }

  private exportXLSX (project: Project, userId: string) {
    if (!project || !project.name || !project.trackedTimes || project.trackedTimes.length < 1) {
      this.$store.dispatch('error/setError', this.$t('timetrack.export.error'))
      return
    }

    const timesHeader = [
      { header: this.$t('timetrack.export.headers.date'), key: "date", width: 10 },
      { header: this.$t('timetrack.export.headers.client'), key: "client", width: 20 },
      { header: this.$t('timetrack.export.headers.description'), key: "task", width: 25 },
      { header: this.$t('timetrack.export.headers.startedAt'), key: "started", width: 10 },
      { header: this.$t('timetrack.export.headers.ended'), key: "stopped", width: 10 },
      { header: this.$t('timetrack.export.headers.duration'), key: "time", width: 15 },
      { header: this.$t('timetrack.export.headers.salary'), key: "total", width: 10 }
    ]
    const expenditureHeader = [
      { header: this.$t('timetrack.project.date'), key: "date", width: 10 },
      { header: this.$t('timetrack.expenditure.quantity'), key: "quantity", width: 10 },
      { header: this.$t('timetrack.expenditure.position'), key: "position", width: 10 },
      { header: this.$t('timetrack.expenditure.positionPrice'), key: "positionPrice", width: 15 },
      { header: this.$t('timetrack.expenditure.totalPrice'), key: "totalPrice", width: 15 }
    ]
    this.accountService.getUserById(userId)
      .then((user) => {
        if (!user || !project.name || !project.trackedTimes) return
        const filename = user.firstName + ' ' + user.lastName + ' - ' +
          this.dateTimeHelper.getReadableDateFromStartingTime(Date.now()) + ' - ' +
          this.$t('timetrack.export.trackingFileName') + ' - ' +
          project.name + ' - ' +
          project.description +
          '.' + this.exportFormat
        const preparedTrackingData = prepareTrackingData(project.name, project.trackedTimes)
        const preparedExpenditureData = prepareExpenditureData(project.trackedTimes)
        exportEntries(timesHeader,
          expenditureHeader,
          preparedTrackingData.exportEntries,
          preparedExpenditureData.expenditureEntries,
          filename,
          this.$t('timetrack.export.worksheet.timetrack') as string,
          this.$t('timetrack.export.worksheet.expenditures') as string
        )
      })
  }

  private exportCSV (project: Project, userId: string) {
    this.csvExportTrackedTimes(project, userId)
    let hasExpenditures = false
    if (project && project.trackedTimes) {
      project.trackedTimes.forEach((time) => {
        if (!hasExpenditures && time.expenditures && time.expenditures.length > 0) {
          hasExpenditures = true
        }
      })
    }
    if (hasExpenditures) {
      this.csvExportExpenditures(project, userId)
    }
  }

  private csvExportTrackedTimes (project: Project, userId: string) {
    if (!project || !project.name || !project.trackedTimes || project.trackedTimes.length < 1) {
      this.$store.dispatch('error/setError', this.$t('timetrack.export.error'))
      return
    }
    const headers = [
      this.$t('timetrack.export.headers.date') as string,
      this.$t('timetrack.export.headers.client') as string,
      this.$t('timetrack.export.headers.description') as string,
      this.$t('timetrack.export.headers.startedAt') as string,
      this.$t('timetrack.export.headers.ended') as string,
      this.$t('timetrack.export.headers.duration') as string,
      this.$t('timetrack.export.headers.salary') as string
    ]
    this.accountService.getUserById(userId)
      .then((user) => {
        if (!user || !project.name || !project.trackedTimes) return
        const filename = user.firstName + ' ' + user.lastName + ' - ' +
          this.dateTimeHelper.getReadableDateFromStartingTime(Date.now()) + ' - ' +
          this.$t('timetrack.export.trackingFileName') + ' - ' +
          project.name + ' - ' +
          project.description +
          '.' + this.exportFormat
        const preparedTrackingData = prepareTrackingData(project.name, project.trackedTimes)
        if (this.exportFormat === 'csv') {
          exportTrackedTimesToCsv(filename, preparedTrackingData.exportEntries, preparedTrackingData.total, preparedTrackingData.totalTime, headers, this.$t('timetrack.project.total') as string)
        }
      })
  }

  private csvExportExpenditures (project: Project, userId: string) {
    if (!project || !project.trackedTimes) return
    const headers = [
      this.$t('timetrack.project.date') as string,
      this.$t('timetrack.expenditure.quantity') as string,
      this.$t('timetrack.expenditure.position') as string,
      this.$t('timetrack.expenditure.positionPrice') as string,
      this.$t('timetrack.expenditure.totalPrice') as string
    ]
    this.accountService.getUserById(userId)
      .then((user) => {
        if (!user || !project.name || !project.trackedTimes) return
        const filename = user.firstName + ' ' + user.lastName + ' - ' +
          this.dateTimeHelper.getReadableDateFromStartingTime(Date.now()) + ' - ' +
          this.$t('timetrack.export.expenditureFileName') + ' - ' +
          project.name + ' - ' +
          project.description +
          '.' + this.exportFormat
        const preparedData = prepareExpenditureData(project.trackedTimes)
        if (this.exportFormat === 'csv') {
          exportExpendituresToCsv(filename, preparedData.expenditureEntries, preparedData.total, headers, this.$t('timetrack.project.total') as string)
        }
      })
  }

  private disableLoadingIfEverythingDownloaded (projectString: string) {
    if (this.projectsToBeDownloaded && this.projectsToBeDownloaded.includes(projectString)) {
      this.projectsToBeDownloaded = this.projectsToBeDownloaded.filter((p) => { return p !== projectString })
      if (this.projectsToBeDownloaded.length < 1) {
        this.loading = false
        this.downloadPdf = false
      }
    }
  }

  private addProjectsToBeDownloaded (projects: string[]) {
    if (projects.length < 1) {
      this.loading = false
      this.downloadPdf = false
      this.$store.dispatch('error/setError', this.$t('timetrack.export.error'))
    } else {
      this.projectsToBeDownloaded = projects
    }
  }
}
